/**
 * Profile Request DTOs
 * Type-safe request interfaces for profile management endpoints
 */

// Note: UpdateProfileRequest and ChangePasswordRequest are in auth contracts

/**
 * Request to upload avatar
 */
export interface AvatarUploadRequest {
  file: File | Blob
  filename?: string
}

/**
 * Request to delete account (requires password confirmation)
 */
export interface DeleteAccountRequest {
  password: string
  confirmation: 'DELETE_MY_ACCOUNT' // Required confirmation string
}

/**
 * Request to update notification preferences
 */
export interface UpdateNotificationPreferencesRequest {
  emailNotifications: boolean
  pushNotifications: boolean
  securityAlerts?: boolean
  systemUpdates?: boolean
  marketingEmails?: boolean
  weeklyReports?: boolean
  monthlyReports?: boolean
}

/**
 * Request to update security settings
 */
export interface UpdateSecuritySettingsRequest {
  // twoFactorEnabled removed for MVP
  sessionTimeout?: number
  loginNotifications?: boolean
  allowMultipleSessions?: boolean
}

// 2FA request interfaces removed for MVP:
// - Enable2FARequest
// - Disable2FARequest
// - Verify2FARequest

/**
 * Profile form data for updating personal information
 */
export interface ProfileFormData {
  firstName?: string
  lastName?: string
  email?: string
  company?: string
  phone?: string
  bio?: string
  timezone?: string
  language?: string
  country?: string
}

/**
 * Security form data for password change
 */
export interface SecurityFormData {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

/**
 * Notification settings configuration
 */
export interface NotificationSettings {
  emailNotifications: boolean
  securityAlerts: boolean
  systemUpdates: boolean
  pushNotifications?: boolean
  marketing?: boolean
  weeklyReports?: boolean
  monthlyReports?: boolean
}

/**
 * Notification-center preferences: the exact shape returned by GET /api/notifications/settings
 * and accepted (partially, as `Partial<NotificationCenterPreferences>`) by
 * PUT /api/notifications/settings.
 */
export interface NotificationCenterPreferences {
  enableInfo: boolean
  enableWarning: boolean
  enableSuccess: boolean
  enableError: boolean
  enableSystemAnnouncements: boolean
  enableImportUpdates: boolean
  enableTaxReports: boolean
  enableAccountAlerts: boolean
  enableCollaboration: boolean
  enableBrowserNotifications: boolean
  enableSound: boolean
  enableEmailDigest: boolean
  emailDigestFrequency: 'none' | 'immediate' | 'hourly' | 'daily' | 'weekly'
}

/**
 * Trade table view modes available in the transactions UI
 */
export type TradeTableView = 'compact' | 'standard' | 'advanced' | 'total' | 'custom'

/**
 * Persisted UI preferences for the transactions trade table.
 * Stored under `users.preferences.tradeTable` JSONB.
 *
 * - `rememberPreferences`: opt-out toggle. When false, frontend should ignore
 *   stored values and use defaults.
 * - `defaultPageSize`: 5 | 10 | 25 | 50 | 100 | 1000 | -1 (-1 = show all rows).
 * - `keyboardShortcutsEnabled`: opt-in toggle for power-user keyboard shortcuts
 *   on the trade table (defaults to false; press ?/Cmd+/ to discover).
 * - `customColumnVisibility`: the column-visibility map (`columnId -> boolean`)
 *   for the user's CUSTOM view. Only meaningful when `defaultView === 'custom'`;
 *   omitted for preset views (whose columns come from the shared presets). It is
 *   persisted so a hand-picked column selection survives a page reload / SPA
 *   navigation instead of reverting to the standard preset.
 */
export interface TradeTablePreferences {
  rememberPreferences: boolean
  defaultView: TradeTableView
  defaultPageSize: number
  advancedSearchOpen: boolean
  keyboardShortcutsEnabled: boolean
  customColumnVisibility?: Record<string, boolean>
}

/**
 * Default trade table preferences applied when the user has not yet saved any.
 */
export const DEFAULT_TRADE_TABLE_PREFERENCES: TradeTablePreferences = {
  rememberPreferences: true,
  defaultView: 'standard',
  defaultPageSize: 10,
  advancedSearchOpen: false,
  keyboardShortcutsEnabled: false,
}

/**
 * Preferences form data
 */
export interface PreferencesFormData {
  timezone: string
  baseCurrency: string
  language: string
  theme: string
  dateFormat: string
  numberFormat: string
  notifications?: {
    email: boolean
    push: boolean
    marketing: boolean
  }
  privacy?: {
    profileVisibility: 'public' | 'private'
    showEmail: boolean
  }
  tradeTable?: TradeTablePreferences
}

/**
 * Password strength indicator
 */
export interface PasswordStrength {
  score: number
  feedback: string
  color: string
  isValid?: boolean
}

/**
 * Profile state for UI state management
 */
export interface ProfileState {
  isLoading: boolean
  isSaving: boolean
  error: string | null
  successMessage: string | null
  hasUnsavedChanges: boolean
}

/**
 * Profile validation errors
 */
export interface ProfileValidationErrors {
  personal?: Record<string, string>
  security?: Record<string, string>
  preferences?: Record<string, string>
  notifications?: Record<string, string>
}

/**
 * Profile tab types for UI navigation
 */
export type ProfileTab = 'personal' | 'preferences' | 'security' | 'notifications' | 'privacy'

/**
 * Base interface for profile requests that require authentication
 */
export interface AuthenticatedProfileRequest {
  // All profile requests implicitly require authentication
  // This is handled at the middleware level
}
